import { Fragment, useEffect, useId, useRef, useState, type CSSProperties, type FormEvent, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { offlineApi, type OfflineMeta, type SaleCounts, type SaleRow } from '../../api/offline.js';
import { Spinner, ErrorBox, EmptyBox, Field, Pagination } from '../../components/ui.js';
import { ConfirmModal, useDialogBehavior } from '../../components/Modal.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { datetime, money, num } from '../../lib/format.js';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
/**
 * 시간대 이름표. 키는 구간의 끝 시각(SALE_HOURS)이다.
 *
 * '11' 은 2026-10-08 에 더한 칸이다. 다른 칸은 3시간 간격이라 기계적으로는
 * '8-11시' 가 되겠지만, 매장이 9시에 열어서 **9-11시**로 적는다(요청 문구 그대로).
 */
const HOUR_LABELS: Record<string, string> = { '11': '9-11시', '14': '11-14시', '17': '14-17시', '20': '17-20시', '23': '20-23시' };
const INTYPES = [
  { value: '', label: '전체 보고' }, { value: 'M', label: '중간 보고' }, { value: 'L', label: '최종 보고' },
];
const WEATHER = [
  { value: '', label: '미입력' }, { value: '1', label: '☀️ 맑음' }, { value: '2', label: '☁️ 흐림' },
  { value: '3', label: '❄️ 눈' }, { value: '4', label: '☔ 비' },
];
/** 매출추가(새 정산)의 날씨 기본값 - 맑음(2026-10-01 요청). 대부분의 날이 맑아 매번 고르는 수고를 던다. */
const DEFAULT_WEATHER = '1';
/**
 * 결제 및 지출 입력 칸. 순서가 그대로 화면 순서이고 Tab 순서다.
 *
 * 2026-10-07 에 계좌이체·직수령이 칸으로 갈라졌다(migrations/021). 그전까지
 * 계좌이체는 적립금과 함께 pay_point 한 칸에 들어가 '계좌/적립금' 이라고
 * 불렸고(레거시 offline_input.php:647 의 이름 그대로였다) 직수령은 받을 자리가
 * 없었다. 지금 pay_point 는 적립금만 뜻한다.
 *
 * 지출(daily_expense)은 결제수단이 아니라 그날 쓴 돈이라 총매출 합계에
 * 들어가지 않는다 - 같은 칸 묶음에 있을 뿐이다(원본도 한 표에 같이 뒀다).
 */
const PAYMENT_FIELDS = [
  { key: 'pay_cash', label: '현금' }, { key: 'pay_card', label: '카드' },
  { key: 'pay_transfer', label: '계좌이체' }, { key: 'pay_point', label: '적립금' },
  { key: 'pay_quick', label: '퀵' }, { key: 'pay_direct', label: '직수령' },
  { key: 'daily_expense', label: '지출' },
] as const;
/** 총매출(pay_total)에 더해지는 칸 - 지출만 빠진다. 원본 offline_exec.php:26 과 같다. */
const PAY_TOTAL_FIELDS = ['pay_cash', 'pay_card', 'pay_transfer', 'pay_point', 'pay_quick', 'pay_direct'];

/**
 * 시간대별 입력의 묶음. **레거시 offline_input.php 의 편성 그대로다**
 * (요청, 2026-10-07) - 한 묶음이 한 표이고 그 안에 두 줄이 들어간다.
 *
 *   팀       : 방문(income_team) / 구매(payment_team)
 *   방문인원 : 남(income_man)    / 여(income_woman)
 *   구매인원 : 남(payment_man)   / 여(payment_woman)
 *
 * 한동안은 이걸 '팀·남성·여성' 세 묶음으로 쪼개 각 묶음에 방문·구매를 뒀는데,
 * 현장에서 세는 단위(팀 수 → 방문 인원 → 구매 인원)와 어긋나 다시 레거시
 * 편성으로 돌렸다. 컬럼은 그대로다 - 묶는 방식만 다르다.
 */
const HOUR_SECTIONS = [
  { label: '팀', unit: '팀', rate: true, rows: [{ prefix: 'income_team', label: '방문' }, { prefix: 'payment_team', label: '구매' }] },
  { label: '방문인원', unit: '명', rate: false, rows: [{ prefix: 'income_man', label: '남' }, { prefix: 'income_woman', label: '여' }] },
  { label: '구매인원', unit: '명', rate: false, rows: [{ prefix: 'payment_man', label: '남' }, { prefix: 'payment_woman', label: '여' }] },
] as const;

function todayISO() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function shiftDate(days: number): string {
  const date = new Date(`${todayISO()}T00:00:00+09:00`);
  date.setDate(date.getDate() + days);
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(date);
}

function monthStart(): string { return `${todayISO().slice(0, 7)}-01`; }
function conversion(income: number, payment: number): string {
  return income > 0 ? `${Math.round((payment / income) * 100)}%` : '-';
}

export function OfflineSalesPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const from = params.get('from') ?? todayISO();
  const to = params.get('to') ?? todayISO();
  const place = params.get('place') ?? '';
  const intype = params.get('intype') ?? '';
  const weekdayParam = params.get('weekday');
  const weekday = weekdayParam === null ? null : Number(weekdayParam);
  const page = Number(params.get('page') ?? 1);
  const [draft, setDraft] = useState({ from, to });
  const [openSeq, setOpenSeq] = useState<number | null>(null);
  const [editing, setEditing] = useState<SaleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<SaleRow | null>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);

  const meta = useQuery({ queryKey: ['offline', 'meta'], queryFn: () => offlineApi.meta() });
  const list = useQuery({
    queryKey: ['offline', 'sales', { from, to, place, intype, weekday, page }],
    queryFn: () => offlineApi.sales({ from, to, place, intype, weekday, page }),
  });

  function apply(next: Record<string, string | number | null> = {}, dates = draft) {
    const q: Record<string, string> = { from: dates.from, to: dates.to };
    const merged = { place, intype, weekday, ...next };
    if (merged.place) q.place = String(merged.place);
    if (merged.intype) q.intype = String(merged.intype);
    if (merged.weekday !== null && merged.weekday !== undefined) q.weekday = String(merged.weekday);
    setParams(q, { replace: true });
    setOpenSeq(null);
  }

  function useRange(nextFrom: string, nextTo: string) {
    const dates = { from: nextFrom, to: nextTo };
    setDraft(dates);
    apply({}, dates);
  }

  function reset() {
    const today = todayISO();
    setDraft({ from: today, to: today });
    setParams({}, { replace: true });
    setOpenSeq(null);
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteError(null);
    try {
      await offlineApi.voidSale(deleting.seq);
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['offline', 'sales'] });
    } catch (error) { setDeleteError(error); }
  }

  const totals = list.data?.totals;
  const purchaseRate = totals ? conversion(totals.incomeTeam, totals.paymentTeam) : '-';

  return (
    <section className="offline-sales-page">
      <header className="page-head offline-sales-head">
        <div><span className="offline-sales-eyebrow">STORE PERFORMANCE</span><h1 className="page-title">오프라인 판매통계</h1>
          <p className="page-desc">매장별 매출과 방문→구매 흐름을 확인하고 정산 내용을 바로 수정합니다.</p></div>
        <div className="page-head__actions">{list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          <a className="btn btn--ghost btn--sm" href="/offline/timetable">매장 타임테이블</a>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>+ 매출추가</button></div>
      </header>

      <div className="offline-sales-overview">
        <OverviewCard label="조회 매출" value={totals ? `${money(totals.payTotal)}원` : '—'} sub={`${num(list.data?.total ?? 0)}건의 정산`} tone="sales" />
        <OverviewCard label="방문 팀" value={totals ? `${num(totals.incomeTeam)}팀` : '—'} sub={`구매 ${num(totals?.paymentTeam ?? 0)}팀`} tone="visit" />
        <OverviewCard label="구매 전환율" value={purchaseRate} sub="방문 팀 대비 구매 팀" tone="conversion" />
        <OverviewCard label="지출" value={totals ? `${money(totals.dailyExpense)}원` : '—'} sub="조회 기간 합계" tone="expense" />
      </div>

      <div className="filters offline-sales-filters">
        <Field label="매장"><select className="input" value={place} onChange={(event) => apply({ place: event.target.value })}>
          <option value="">전체 매장</option>{(meta.data?.salePlaces ?? []).map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
        <Field label="보고 단계"><select className="input" value={intype} onChange={(event) => apply({ intype: event.target.value })}>
          {INTYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></Field>
        <Field label="시작일"><input className="input" type="date" value={draft.from} onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))} /></Field>
        <Field label="종료일"><input className="input" type="date" value={draft.to} onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))} /></Field>
        <div className="filters__buttons"><button type="button" className="btn btn--primary" onClick={() => apply()}>검색</button><button type="button" className="btn btn--ghost" onClick={reset}>초기화</button></div>
      </div>

      <div className="offline-sales-filterbar">
        <div className="offline-sales-presets" aria-label="빠른 기간"><span>빠른 기간</span>
          <button type="button" onClick={() => useRange(todayISO(), todayISO())}>오늘</button>
          <button type="button" onClick={() => useRange(shiftDate(-6), todayISO())}>최근 7일</button>
          <button type="button" onClick={() => useRange(monthStart(), todayISO())}>이번 달</button></div>
        <div className="offline-sales-weekbar toolbar" aria-label="요일 필터"><span>요일</span>{WEEKDAYS.map((label, index) => (
          <button key={index} type="button" className={weekday === index ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            aria-pressed={weekday === index} onClick={() => apply({ weekday: weekday === index ? null : index })}>{label}</button>))}</div>
      </div>

      {list.isPending ? <Spinner /> : list.isError ? <ErrorBox error={list.error} onRetry={() => void list.refetch()} /> :
        list.data.rows.length === 0 ? <EmptyBox>이 조건에는 정산 자료가 없습니다.</EmptyBox> : <>
          <div className="table-wrap offline-sales-table-wrap"><table className="table offline-sales-table">
            <thead><tr><th>일자 / 매장</th><th>보고</th><th>근무자 / 날씨</th><th style={{ textAlign: 'right' }}>매출 / 객단가</th>
              <th>결제 구성</th><th style={{ textAlign: 'right' }}>지출</th><th>방문 → 구매</th><th>성별 구매</th><th>등록</th><th>관리</th></tr></thead>
            <tbody>{list.data.rows.map((row) => <Fragment key={row.seq}>
              <tr className={`offline-sales-row${row.intype === 'L' ? ' offline-sales-row--final' : ''}${openSeq === row.seq ? ' offline-sales-row--open' : ''}`}
                onClick={() => setOpenSeq(openSeq === row.seq ? null : row.seq)}>
                <td><div className="offline-sales-identity"><strong>{row.saleDate} <small className={row.weekday === 0 ? 'td-danger' : row.weekday === 6 ? 'adw-sat' : ''}>({WEEKDAYS[row.weekday]})</small></strong><span>{row.place}</span></div></td>
                <td>{row.intype === 'L' ? <span className="badge badge--ok">최종</span> : row.intype === 'M' ? <span className="badge badge--info">중간</span> : <span className="td-muted">미지정</span>}</td>
                <td><div className="offline-sales-worker"><strong>{row.worker1 || '-'}</strong><span>{row.worker2 ? `${row.worker2} · ` : ''}{row.weather || '날씨 미입력'}</span></div></td>
                <td className="offline-sales-revenue"><strong>{money(row.payTotal)}원</strong><span>객단가 {row.perTeam === null ? '-' : `${money(row.perTeam)}원`}</span></td>
                <td><PaymentMix row={row} /></td><td className="offline-sales-expense">{money(row.dailyExpense)}원</td>
                <td><div className="offline-sales-funnel"><strong>{num(row.team.income)} → {num(row.team.payment)}팀</strong><span>{conversion(row.team.income, row.team.payment)} 전환</span></div></td>
                <td><div className="offline-sales-gender"><span>남 <b>{num(row.man.payment)}</b></span><span>여 <b>{num(row.woman.payment)}</b></span></div></td>
                <td className="offline-sales-regdate">{datetime(row.regdate)}</td><td><div className="offline-sales-actions">
                  <button type="button" className="btn btn--ghost btn--sm" onClick={(event) => { event.stopPropagation(); setEditing(row); }}>수정</button>
                  <button type="button" className="btn btn--ghost btn--sm offline-sales-actions__delete" onClick={(event) => { event.stopPropagation(); setDeleteError(null); setDeleting(row); }}>삭제</button>
                </div></td></tr>
              {openSeq === row.seq && <tr className="offline-sales-detail-row"><td colSpan={10}><HourDetail row={row} /></td></tr>}
            </Fragment>)}</tbody>
            {totals && <tfoot><tr className="row--total"><td colSpan={3}>전체 합계 ({num(list.data.total)}건)</td><td className="td-right">{money(totals.payTotal)}원</td>
              <td><span className="offline-sales-totalmix">현금 {money(totals.payCash)} · 카드 {money(totals.payCard)}</span></td><td className="td-right">{money(totals.dailyExpense)}원</td>
              <td>{num(totals.incomeTeam)} → {num(totals.paymentTeam)}팀</td><td>남 {num(totals.paymentMan)} · 여 {num(totals.paymentWoman)}</td><td colSpan={2} /></tr></tfoot>}
          </table></div>
          <Pagination page={list.data.page} totalPages={list.data.totalPages} total={list.data.total} onChange={(nextPage) => {
            const next = new URLSearchParams(params); next.set('page', String(nextPage)); setParams(next, { replace: true }); setOpenSeq(null);
          }} />
        </>}

      {creating && meta.data && <SaleEditModal mode="create" meta={meta.data} onClose={() => setCreating(false)} />}
      {editing && meta.data && <SaleEditModal mode="edit" row={editing} meta={meta.data} onClose={() => setEditing(null)} />}
      {deleting && (
        <ConfirmModal
          title="이 정산을 삭제하시겠습니까?"
          message={<>
            <b>{deleting.saleDate} · {deleting.place}</b> (총매출 {money(deleting.payTotal)}원 · {deleting.worker1 || '근무자 미입력'})<br />
            삭제한 정산은 목록에서 제외되며 이 화면에서는 되돌릴 수 없습니다.
          </>}
          confirmLabel="삭제하기"
          error={deleteError ? <ErrorBox error={deleteError} /> : undefined}
          onClose={() => setDeleting(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </section>
  );
}

function OverviewCard({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: string }) {
  return <div className={`offline-sales-metric offline-sales-metric--${tone}`}><span>{label}</span><strong>{value}</strong><small>{sub}</small></div>;
}

/** 카드·현금은 늘 보여준다(결제의 대부분이다). 나머지는 값이 있을 때만. */
const PAYMIX_EXTRA = [
  { label: '계좌', pick: (row: SaleRow) => row.payTransfer },
  { label: '적립', pick: (row: SaleRow) => row.payPoint },
  { label: '퀵', pick: (row: SaleRow) => row.payQuick },
  { label: '직수령', pick: (row: SaleRow) => row.payDirect },
] as const;

function PaymentMix({ row }: { row: SaleRow }) {
  const base = Math.max(row.payTotal, 1);
  const cardRate = Math.min(100, (row.payCard / base) * 100);
  const cashRate = Math.min(100 - cardRate, (row.payCash / base) * 100);
  const extra = PAYMIX_EXTRA.filter((item) => item.pick(row) !== 0);
  return <div className="offline-sales-paymix"><div><i className="offline-sales-paymix__card" style={{ width: `${cardRate}%` }} /><i className="offline-sales-paymix__cash" style={{ width: `${cashRate}%` }} /></div>
    <span><CopyAmount label="카드" amount={row.payCard} /><CopyAmount label="현금" amount={row.payCash} /></span>
    {extra.length > 0 && <small>{extra.map((item) => <CopyAmount key={item.label} label={item.label} amount={item.pick(row)} />)}</small>}</div>;
}

/**
 * 누르면 금액만 클립보드로 넣는다 (요청, 2026-10-07).
 *
 * 넣는 값은 **숫자만**(쉼표도 '원'도 없다) - 받는 쪽이 대개 다른 입력칸이나
 * 계산기라, 보이는 '947,800' 을 그대로 넣으면 거기서 다시 지워야 한다.
 *
 * 줄 전체에 '펼치기' onClick 이 걸려 있어서 stopPropagation 이 필요하다 -
 * 안 하면 복사할 때마다 시간대별 내역이 열리고 닫힌다.
 */
function CopyAmount({ label, amount }: { label: string; amount: number }) {
  const [copied, setCopied] = useState(false);
  // '복사됨' 표시를 1.5초 뒤에 되돌린다. 그 전에 줄이 사라질 수 있어(조회 조건을
  // 바꾸면 표가 새로 그려진다) 타이머를 치워 둔다.
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  async function copy(event: MouseEvent) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(amount));
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드를 못 쓰는 환경(권한 거부·비 HTTPS)이면 아무 일도 없다 - 숫자는 화면에 그대로 있다.
    }
  }
  return (
    <button type="button" className="offline-sales-paymix__copy" data-copied={copied || undefined}
      title={`${label} ${money(amount)}원 복사`} aria-label={`${label} ${money(amount)}원 복사`} onClick={(event) => void copy(event)}>
      {label} <b>{money(amount)}</b>{copied && <i aria-hidden="true">✓</i>}
    </button>
  );
}

function HourDetail({ row }: { row: SaleRow }) {
  const groups: { label: string; counts: SaleCounts }[] = [
    { label: '팀', counts: row.team }, { label: '남', counts: row.man }, { label: '여', counts: row.woman },
  ];
  return <div className="offline-sales-detail"><div className="offline-sales-detail__head"><div><span>HOURLY FLOW</span><strong>{row.saleDate} · {row.place}</strong></div><small>각 값은 방문 / 구매 순서입니다.</small></div>
    <table className="table table--nested table--dense"><thead><tr><th>구분</th>{row.team.byHour.map((item) => <th key={item.hour}>{HOUR_LABELS[item.hour] ?? `${item.hour}시`}</th>)}<th>합계</th><th>전환율</th></tr></thead>
      <tbody>{groups.map((group) => <tr key={group.label}><td><b>{group.label}</b></td>{group.counts.byHour.map((item) => <td key={item.hour}>{num(item.income)} / {num(item.payment)}</td>)}
        <td><b>{num(group.counts.income)} / {num(group.counts.payment)}</b></td><td>{conversion(group.counts.income, group.counts.payment)}</td></tr>)}</tbody></table></div>;
}

type SaleForm = { worker1: string; worker2: string; inweather: string; numbers: Record<string, string> };

/**
 * 정산 넣기·고치기 (원본 offline_input.php).
 *
 * 두 모드가 폼의 대부분을 공유한다 - 다른 건 '어느 정산인가'를 정하는 위쪽뿐이다.
 *   create : 매장·일자·보고 단계를 직접 고른다 (원본의 mode=insert)
 *   edit   : 그 셋은 고정이고 내용만 고친다 (원본의 mode=update)
 *
 * 서버 saveSale 은 (매장, 일자, 보고 단계)가 이미 있으면 새로 넣지 않고 고친다.
 * 그래서 create 모드에서 셋을 다 고르면 기존 정산이 있는지 미리 확인해
 * "덮어쓴다"는 사실을 알려준다 - 모르고 지난 정산을 날리는 일을 막는다.
 */
function SaleEditModal({
  mode, row, meta, onClose,
}: {
  mode: 'create' | 'edit';
  row?: SaleRow;
  meta: OfflineMeta;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isCreate = mode === 'create';
  const { user } = useAuth();

  // create 는 사용자가 고르고, edit 은 그 행의 값으로 고정된다.
  const [target, setTarget] = useState({
    place: row?.place ?? '',
    saleDate: row?.saleDate ?? todayISO(),
    intype: row?.intype ?? '',
  });
  const ready = target.place !== '' && target.intype !== '' && target.saleDate !== '';

  // edit 은 그 정산을 읽어 채우고, create 는 고른 조합에 이미 있는지 확인한다.
  const raw = useQuery({
    queryKey: ['offline', 'sale', target.place, target.saleDate, target.intype],
    queryFn: () => offlineApi.sale(target.place, target.saleDate, target.intype),
    enabled: ready,
  });

  const [draft, setDraft] = useState<SaleForm | null>(null);
  const existing = raw.data ?? null;
  // 매출추가(빈 폼)일 때만 주 근무자에 지금 로그인한 사람 이름을 기본값으로
  // 채운다(2026-10-01 요청) - 매장을 고른 조합에 이미 정산이 있으면(existing)
  // 그 기록의 실제 근무자를 그대로 보여줘야 하므로 blank 자체를 안 쓴다.
  // Layout.tsx 의 topbar 이름과 같은 규칙(user?.name ?? user?.userid) 이다.
  const blank = blankForm(meta, user?.name ?? user?.userid ?? '');
  const source = draft ?? (existing ? formFromRaw(existing, meta) : isCreate ? blank : null);
  // create 인데 이미 있는 조합이면, 저장이 새로 넣는 게 아니라 덮어쓰기가 된다.
  const willOverwrite = isCreate && ready && !raw.isPending && existing !== null;

  const save = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error('정산 내용을 불러오지 못했습니다.');
      return offlineApi.saveSale({
        place: target.place, saleDate: target.saleDate, intype: target.intype,
        worker1: source.worker1.trim(), worker2: source.worker2.trim(),
        inweather: source.inweather, numbers: normalizedNumbers(source.numbers, meta),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['offline', 'sales'] });
      void qc.invalidateQueries({ queryKey: ['offline', 'sale', target.place, target.saleDate, target.intype] });
      onClose();
    },
  });

  function change(mutator: (next: SaleForm) => void) {
    if (!source) return;
    const next = { ...source, numbers: { ...source.numbers } }; mutator(next); setDraft(next);
  }

  function setTargetField(patch: Partial<typeof target>) {
    setTarget((current) => ({ ...current, ...patch }));
    // 대상이 바뀌면 그 조합의 값을 새로 받아야 한다 - 이전 입력을 끌고 가지 않는다.
    setDraft(null);
  }

  // 원본 offline_input.php:396-415 의 필수 항목 그대로. 예전에는 빠진 항목
  // 이름만 바닥에 모아 보여주고 저장 버튼을 잠갔다 - 어느 칸이 문제인지
  // 한눈에 안 보였다. 지금은 칸마다 메시지를 붙이고(아래 Field error), 저장
  // 버튼은 열어 둔 채 누르면 첫 번째 빈 칸으로 초점을 옮긴다.
  const errors: Record<string, string> = {};
  if (target.place === '') errors.place = '매장 지점을 고르세요.';
  if (target.intype === '') errors.intype = '보고 시간을 고르세요.';
  if (source && source.worker1.trim() === '') errors.worker1 = '주 근무자를 입력하세요.';
  if (source && source.inweather === '') errors.inweather = '날씨를 고르세요.';
  const [submitted, setSubmitted] = useState(false);
  const shown = submitted ? errors : {};
  const formRef = useRef<HTMLFormElement>(null);
  const panel = useDialogBehavior<HTMLDivElement>(onClose);
  const titleId = useId();

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) {
      const first = Object.keys(errors)[0]!;
      const el = formRef.current?.querySelector<HTMLElement>(`[data-field="${first}"]`)
        ?? panel.current?.querySelector<HTMLElement>(`[data-field="${first}"]`);
      el?.focus();
      return;
    }
    save.mutate();
  }

  const total = source ? displayedPayTotal(source.numbers, meta) : 0;
  const clamped = source != null && !meta.negativeAllowedPayFields.includes('pay_total') && paymentTotal(source.numbers) < 0;

  // 배경(빈 화면)을 눌러도 닫히지 않는다(2026-11-04 요청) - 입력하다 바깥을
  // 잘못 눌러 적던 정산이 통째로 날아가는 일이 있었다. 닫기는 취소·저장(·닫기 X)으로만.
  return <div className="modal-overlay sale-editor-overlay">
    <div ref={panel} className="sale-editor" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header className="sale-editor__head">
        <div className="sale-editor__heading">
          <span className="sale-editor__eyebrow">오프라인 매장 정산</span>
          <h2 id={titleId}>{isCreate ? '매출 추가' : '정산 수정'}</h2>
          <p>{isCreate
            ? '매장과 보고 단계를 고른 뒤 그날 매출을 입력합니다.'
            : `${target.saleDate} · ${target.place} · ${target.intype === 'L' ? '최종 보고' : target.intype === 'M' ? '중간 보고' : '보고 미지정'}`}</p>
        </div>
        <button type="button" className="sale-editor__close" onClick={onClose} aria-label="닫기">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.5 5.5l9 9m0-9l-9 9" /></svg>
        </button>
      </header>

      <form ref={formRef} className="sale-editor__form" onSubmit={submit} noValidate>
        <div className="sale-editor__body">
          {isCreate && (
            <section className="sale-card">
              <div className="sale-card__head"><h3>어느 정산인가요?</h3><p>매장 · 일자 · 보고 단계</p></div>
              <div className="sale-grid sale-grid--target">
                <Field label="매장 지점" error={shown.place}>
                  <select className="input" data-field="place" aria-invalid={shown.place ? true : undefined}
                    value={target.place} onChange={(event) => setTargetField({ place: event.target.value })}>
                    <option value="">선택하세요</option>
                    {meta.saleInputPlaces.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </Field>
                <Field label="일자">
                  <input className="input" type="date" value={target.saleDate} onChange={(event) => setTargetField({ saleDate: event.target.value })} />
                </Field>
                <Field label="보고 시간" error={shown.intype}>
                  <select className="input" data-field="intype" aria-invalid={shown.intype ? true : undefined}
                    value={target.intype} onChange={(event) => setTargetField({ intype: event.target.value })}>
                    <option value="">선택하세요</option>
                    <option value="M">중간보고 (16시 이전)</option>
                    <option value="L">마감보고 (23시 이전)</option>
                  </select>
                </Field>
              </div>
              {willOverwrite && (
                <p className="sale-notice sale-notice--warn" role="status">
                  이 매장·일자·보고 단계에는 <b>이미 정산이 있습니다.</b> 저장하면 새로 만들지 않고 그 정산을 덮어씁니다.
                </p>
              )}
            </section>
          )}

          {!ready ? <EmptyBox>매장과 보고 단계를 먼저 고르세요.</EmptyBox>
            : raw.isPending ? <Spinner label="정산 내용을 불러오는 중…" />
            : raw.isError ? <ErrorBox error={raw.error} onRetry={() => void raw.refetch()} />
            : !source ? <EmptyBox>수정할 정산을 찾지 못했습니다.</EmptyBox> : <>
            <section className="sale-card">
              <div className="sale-card__head"><h3>기본 정보</h3><p>근무자와 날씨</p></div>
              <div className="sale-grid sale-grid--basic">
                <Field label="주 근무자" error={shown.worker1}>
                  <input className="input" data-field="worker1" aria-invalid={shown.worker1 ? true : undefined}
                    value={source.worker1} onChange={(event) => change((next) => { next.worker1 = event.target.value; })} />
                </Field>
                <Field label="보조 근무자">
                  <input className="input" value={source.worker2} onChange={(event) => change((next) => { next.worker2 = event.target.value; })} />
                </Field>
                <Field label="날씨" error={shown.inweather}>
                  <select className="input" data-field="inweather" aria-invalid={shown.inweather ? true : undefined}
                    value={source.inweather} onChange={(event) => change((next) => { next.inweather = event.target.value; })}>
                    {WEATHER.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </Field>
              </div>
            </section>

            <section className="sale-card">
              <div className="sale-card__head"><h3>결제 및 지출</h3><p>총매출은 결제수단 합계로 자동 계산됩니다</p></div>
              <div className="sale-grid sale-grid--pay">
                {PAYMENT_FIELDS.map(({ key, label }) =>
                  <Field key={key} label={label}>
                    <NumberInput ariaLabel={label} value={source.numbers[key] ?? ''}
                      allowNegative={meta.negativeAllowedPayFields.includes(key)}
                      onChange={(value) => change((next) => { next.numbers[key] = value; })} suffix="원" />
                  </Field>)}
              </div>
              {clamped && <p className="sale-notice sale-notice--warn" role="status">결제수단 합계가 0보다 작아 저장 시 0원으로 기록됩니다.</p>}
            </section>

            <section className="sale-card">
              <div className="sale-card__head"><h3>시간대별 방문 · 구매</h3><p>팀 · 방문인원 · 구매인원 순서로, 합계는 입력값에서 자동 계산됩니다</p></div>
              <div className="sale-groups">
                {HOUR_SECTIONS.map((section) => (
                  <HourMatrix key={section.label} label={section.label} rows={section.rows} unit={section.unit} rate={section.rate}
                    hours={meta.saleHours} numbers={source.numbers}
                    onChange={(key, value) => change((next) => { next.numbers[key] = value; })} />
                ))}
              </div>
            </section>

            {save.isError && <ErrorBox error={save.error} />}
          </>}
        </div>

        <footer className="sale-editor__footer">
          <div className="sale-editor__total">
            <span>계산된 총매출</span>
            <strong className={total < 0 ? 'td-danger' : undefined}>{money(total)}<i>원</i></strong>
          </div>
          <div className="sale-editor__actions">
            {submitted && Object.keys(errors).length > 0 && (
              <span className="sale-editor__hint td-danger" role="alert">필수 항목을 채워주세요</span>
            )}
            <button type="button" className="btn btn--ghost" onClick={onClose}>취소</button>
            {/* 비활성 버튼은 "왜 못 누르는지"를 못 알려준다 - 늘 누를 수 있게
                두고, 누르면 submit 이 비어 있는 칸으로 초점을 옮긴다. */}
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>
              {save.isPending ? '저장 중…' : isCreate ? (willOverwrite ? '덮어쓰기 저장' : '매출 저장') : '변경사항 저장'}
            </button>
          </div>
        </footer>
      </form>
    </div>
  </div>;
}

/**
 * 결제 및 지출 칸은 0 대신 공란으로 보여준다(2026-11-04 요청) - '0' 이 미리
 * 들어 있으면 지우고 다시 쳐야 했다. 공란은 저장할 때 0 으로 들어간다
 * (normalizedNumbers 의 `Number('') || 0`).
 */
const PAYMENT_KEYS = new Set<string>(PAYMENT_FIELDS.map((field) => field.key));

/**
 * create 모드의 빈 폼. 숫자 칸은 전부 공란에서 시작한다 - 결제 및 지출(2026-11-04)에
 * 이어 시간대별 방문 · 구매 칸도 '0' 을 지우고 다시 쳐야 했다(2026-11-05 요청).
 * 공란은 저장할 때 0 으로 들어가고, 합계·총매출도 공란을 0 으로 센다.
 * defaultWorker1 은 로그인한 사람 이름(현장에서 직접 입력하는 매장 근무자와는
 * 다른 뜻이지만, 대부분 등록하는 사람 자신이 그날 근무자라 기본값으로 채워
 * 두면 손이 덜 간다) - 그대로 둬도 되고 지우고 다른 이름을 적어도 된다.
 */
function blankForm(meta: OfflineMeta, defaultWorker1 = ''): SaleForm {
  const numbers: Record<string, string> = {};
  for (const key of [...meta.saleColumns, 'daily_expense']) numbers[key] = '';
  return { worker1: defaultWorker1, worker2: '', inweather: DEFAULT_WEATHER, numbers };
}

function formFromRaw(raw: Record<string, unknown>, meta: OfflineMeta): SaleForm {
  const numbers: Record<string, string> = {};
  for (const key of [...meta.saleColumns, 'daily_expense']) {
    const value = Number(raw[key] ?? 0);
    numbers[key] = PAYMENT_KEYS.has(key) && value === 0 ? '' : String(value);
  }
  return { worker1: String(raw.worker1 ?? ''), worker2: String(raw.worker2 ?? ''), inweather: String(raw.inweather ?? ''), numbers };
}
function paymentTotal(numbers: Record<string, string>): number { return PAY_TOTAL_FIELDS.reduce((sum, key) => sum + (Number(numbers[key]) || 0), 0); }
/**
 * 화면에 보여줄 총매출(계산된 그대로, 저장될 값과 같다).
 *
 * pay_total 컬럼은 2026-10-01 부터 signed 다(사용자 요청으로 ALTER TABLE 실행 -
 * offline.service.ts 의 NEGATIVE_ALLOWED_PAY_FIELDS 주석 참고) - 그래서 음수도
 * 그대로 저장된다. meta.negativeAllowedPayFields 를 보고 판단하므로, 혹시 나중에
 * 컬럼이 다시 unsigned 로 바뀌어도(서버가 그렇게 알려주면) 여기서 자동으로 0 에서
 * 막는 예전 동작으로 돌아간다 - 화면에 하드코딩해 두지 않는다.
 */
function displayedPayTotal(numbers: Record<string, string>, meta: OfflineMeta): number {
  const total = paymentTotal(numbers);
  return meta.negativeAllowedPayFields.includes('pay_total') ? total : Math.max(0, total);
}
function normalizedNumbers(numbers: Record<string, string>, meta: OfflineMeta): Record<string, number> {
  const negativeAllowed = new Set(meta.negativeAllowedPayFields);
  const out = Object.fromEntries(
    [...meta.saleColumns, 'daily_expense'].map((key) => {
      const n = Math.trunc(Number(numbers[key]) || 0);
      return [key, negativeAllowed.has(key) ? n : Math.max(0, n)];
    }),
  );
  out.pay_total = displayedPayTotal(numbers, meta);
  for (const kind of ['income', 'payment']) for (const group of meta.saleGroups) out[`${kind}_${group}`] = meta.saleHours.reduce((sum, hour) => sum + (out[`${kind}_${group}_${hour}`] ?? 0), 0);
  return out;
}
/**
 * `-` 는 허용 칸(allowNegative, 결제·지출)에서만 맨 앞 1개를 남기고, 나머지는
 * 전부 숫자만 남긴다. 중간에 찍힌 `-`(예: "12-34")는 legacy 와 같이 지운다 -
 * 자리만 음수인 셈이라 뜻이 없다.
 *
 * `-` 하나만 쳐진 상태도 그대로 둔다 - 사람은 부호부터 치고 숫자를 잇는다.
 * 예전에는 숫자가 없으면 `-` 를 지워서 키보드로는 음수를 아예 칠 수 없었다
 * (2026-11-04 제보). 그대로 저장하면 `Number('-')` 가 NaN 이라 0 으로 들어간다.
 */
function sanitizeNumberInput(value: string, allowNegative: boolean): string {
  const negative = allowNegative && value.trim().startsWith('-');
  const digits = value.replace(/[^0-9]/g, '');
  return negative ? `-${digits}` : digits;
}
function NumberInput({ ariaLabel, value, onChange, suffix, allowNegative = false }: { ariaLabel: string; value: string; onChange: (value: string) => void; suffix?: string; allowNegative?: boolean }) {
  return <div className="offline-sales-number"><input className="input" aria-label={ariaLabel} inputMode={allowNegative ? 'text' : 'numeric'} value={value} onChange={(event) => onChange(sanitizeNumberInput(event.target.value, allowNegative))} />{suffix && <span>{suffix}</span>}</div>;
}

/**
 * 시간대별 입력 한 묶음 (팀 / 방문인원 / 구매인원 - HOUR_SECTIONS 참고).
 *
 * 두 줄과 시간대 네 칸이 한 표다. 줄이 무엇인지는 부르는 쪽이 정한다
 * (팀이면 방문·구매, 인원이면 남·여) - 레거시도 묶음마다 줄 이름이 달랐다.
 * 합계 칸은 네 시간대의 합이라 읽기 전용이다(레거시의 readonly '총' 칸).
 *
 * **DOM 은 시간대 단위로 짠다** - 칸이 "11-14 첫째 줄 → 11-14 둘째 줄 →
 * 14-17 첫째 줄" 순서로 놓여 Tab 이 그대로 그 순서로 넘어간다(요청,
 * 2026-10-01). 현장에서 한 시간대의 두 값을 같이 적기 때문에 줄 단위(윗줄
 * 전부 → 아랫줄 전부)보다 이 쪽이 손이 덜 간다. 보이는 모양(가로 표)은 그대로고
 * 자리만 CSS grid 가 --col/--rowm 으로 잡는다 - tabIndex 를 양수로 주면 페이지
 * 전체 탭 순서를 가로채므로 쓰지 않는다.
 */
function HourMatrix({ label, rows, unit, rate, hours, numbers, onChange }: {
  label: string;
  rows: readonly { prefix: string; label: string }[];
  unit: string;
  /** 두 줄이 방문·구매일 때만(팀) 전환율을 머리에 보여준다. */
  rate: boolean;
  hours: string[];
  numbers: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  const sum = (prefix: string) => hours.reduce((acc, hour) => acc + (Number(numbers[`${prefix}_${hour}`]) || 0), 0);
  const totals = rows.map((row) => sum(row.prefix));
  const last = hours.length + 2;
  return (
    <div className="sale-group" style={{ '--hours': hours.length } as CSSProperties}>
      <div className="sale-group__head">
        <strong>{label}</strong>
        {rate
          ? <span className="sale-group__rate">{conversion(totals[0] ?? 0, totals[1] ?? 0)} 전환</span>
          : <span className="sale-group__rate">{num((totals[0] ?? 0) + (totals[1] ?? 0))}{unit}</span>}
      </div>
      <div className="sale-group__grid">
        <span className="sale-group__corner" aria-hidden="true" />
        {rows.map((row, r) => <span key={row.prefix} className={`sale-group__kind sale-group__kind--r${r + 1}`}>{row.label}</span>)}
        <span className="sale-group__hour sale-group__hour--total" style={{ '--col': last, '--rowm': last } as CSSProperties}>합계</span>

        {hours.map((hour, i) => {
          const place = { '--col': i + 2, '--rowm': i + 2 } as CSSProperties;
          return (
            <Fragment key={hour}>
              <span className="sale-group__hour" style={place}>{HOUR_LABELS[hour] ?? `${hour}시`}</span>
              {rows.map((row, r) => {
                const key = `${row.prefix}_${hour}`;
                return (
                  <div key={row.prefix} className={`sale-group__cell sale-group__cell--r${r + 1}`} style={place}>
                    <NumberInput ariaLabel={`${label} ${row.label} ${HOUR_LABELS[hour] ?? hour}`}
                      value={numbers[key] ?? ''} onChange={(value) => onChange(key, value)} />
                  </div>
                );
              })}
            </Fragment>
          );
        })}

        {rows.map((row, r) => (
          <b key={row.prefix} className={`sale-group__total sale-group__cell--r${r + 1}`}
            style={{ '--col': last, '--rowm': last } as CSSProperties}>{num(totals[r] ?? 0)}</b>
        ))}
      </div>
    </div>
  );
}
